import axios from 'axios'
import { logger } from '@/utils/logger'
import { getAppropriateLoginPath, clearAuthData } from '@/utils/auth'

const api = axios.create({
  timeout: 10_000,
  headers: { 
    'Content-Type': 'application/json'
  }
})


// 請求攔截器
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // 自動添加當前 vendor context
    const userStr = localStorage.getItem('auth_user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        if (user?.currentVendorId) {
          config.headers['X-Current-Vendor-ID'] = user.currentVendorId
        }
      } catch (error) {
        logger.error('解析用戶資訊失敗', error)
      }
    }
    
    // 🔧 如果是 FormData，移除 Content-Type 讓瀏覽器自動設定
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    // Debug: 記錄請求資訊
    logger.debug('[API Request]', {
      url: config.url,
      method: config.method?.toUpperCase(),
      data: config.data,
      params: config.params,
      headers: config.headers
    })
    
    return config
  },
  (error) => {
    logger.error('[API Request Error]', error)
    return Promise.reject(new Error(error))
  }
)

// 回應攔截器
api.interceptors.response.use(
  (response) => {
    // Debug: 記錄回應資訊
    logger.debug('[API Response]', {
      url: response.config.url,
      method: response.config.method?.toUpperCase(),
      status: response.status,
      statusText: response.statusText,
      data: response.data
    })
    
    return response
  },
  (error) => {
    // Debug: 記錄錯誤資訊
    logger.error('[API Response Error]', {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      data: error.response?.data
    })
    
    // 統一錯誤處理
    if (error.response?.status === 401) {
      const data = error.response.data
      const loginPath = getAppropriateLoginPath()
      
      if (data?.code === "PLATFORM_AUTH_EXPIRED") {
        // 平台認證過期 - 顯示友善提示並直接跳轉
        logger.warn('平台認證已過期，導向登入頁面')
        
        // 清除認證資料
        clearAuthData()
        
        // 設置全域標誌避免其他錯誤處理
        ;(window as any).__PLATFORM_AUTH_EXPIRED__ = true
        
        // 10秒後清除標誌（防止影響後續正常操作）
        setTimeout(() => {
          delete (window as any).__PLATFORM_AUTH_EXPIRED__
        }, 10000)
        
        // 使用動態導入顯示訊息並跳轉
        import('./MessageService').then(({ messageService }) => {
          messageService.warning('活動平台認證已過期，即將導向登入頁面...')
          
          // 短暫延遲後跳轉，讓使用者看到訊息
          setTimeout(() => {
            window.location.href = loginPath
          }, 1500)
        }).catch(() => {
          // 降級處理 - 直接跳轉
          console.warn('🔒 活動平台認證已過期，導向登入頁面')
          setTimeout(() => {
            window.location.href = loginPath
          }, 1000)
        })
        
        // 對於平台認證過期，不要讓錯誤繼續傳播
        // 返回一個永遠不會 resolve 的 Promise，阻止後續處理
        return new Promise(() => {})
      } else {
        // 一般 401 錯誤 - 直接跳轉
        logger.warn('認證失效，導向登入頁面:', loginPath)
        clearAuthData()
        window.location.href = loginPath
      }
    }
    return Promise.reject(error)  // 保留原始的 axios 錯誤對象
  }
)

export default api
